import type { IQuestion } from '@repo/types/questions/Question.interface';
import type {
  AnswerQuestionMatching,
  AnswerUserQuestionMatching,
  ExportAnswerMatching,
  FeedbackAnswerQuestionMatching,
  FeedbackAnswerQuestionMatchingItem,
  QuestionMatchingContent,
  QuestionMatchingData,
  QuestionMatchingSerialized,
} from '@repo/types/questions/QuestionMatching';
import type { CorrectType } from '@repo/types/quiz';

import { QuestionType } from '@repo/types/enums/questionType';

export class QuestionMatching implements IQuestion {
  readonly type: QuestionType;
  readonly content: string;
  private readonly points: number = 0;
  private answers: AnswerQuestionMatching[];
  private selectedAnswer: AnswerUserQuestionMatching | null = null;
  private marked: FeedbackAnswerQuestionMatching | null = null;

  constructor(data: QuestionMatchingData, selectedAnswer: AnswerUserQuestionMatching | null, marked: FeedbackAnswerQuestionMatching | null) {
    this.type = QuestionType.MATCHING;
    this.content = data.content;
    this.points = data.points;
    this.answers = data.answers;
    this.selectedAnswer = selectedAnswer;
    this.marked = marked;
  }

  getContent(): QuestionMatchingContent {
    return {
      content: this.content,
      answers: this.answers,
    };
  }

  getMaxPoints(): number {
    return this.points;
  }

  getPoints(): number {
    if (this.marked == null || this.marked.answers == null) return 0;
    return this.marked.answers.reduce((sum, marked) => {
      sum += marked.points ?? 0;
      return sum;
    }, 0);
  }

  setAnswer(answers: AnswerUserQuestionMatching): void {
    this.selectedAnswer = answers;
  }

  getAnswer(): AnswerUserQuestionMatching | null {
    return this.selectedAnswer;
  }

  isAnswered(): boolean {
    return this.selectedAnswer ? this.selectedAnswer.length > 0 : false;
  }

  resetAnswer(): void {
    this.selectedAnswer = null;
  }

  setMarked(marked: FeedbackAnswerQuestionMatching): void {
    this.marked = marked;
  }

  getMarked(): FeedbackAnswerQuestionMatching | null {
    return this.marked;
  }

  isMarked(): boolean {
    return !(this.marked == null);
  }

  correctState(): CorrectType {
    if (!this.isAnswered()) return null;

    if (this.marked && this.marked.answers) {
      const userAnswers: FeedbackAnswerQuestionMatchingItem[] = this.marked.answers;
      const total: number = this.answers.length;
      const correctCount: number = userAnswers.filter((a) => a.correct).length;
      if (correctCount === total) return true;
      if (correctCount > 0) return 'partially';
      return false;
    }

    const points: number = this.getPoints();
    const maxPoints: number = this.getMaxPoints();

    if (points == 0) return false;
    return points == maxPoints ? true : 'partially';
  }

  toJSON(): QuestionMatchingSerialized {
    return {
      marked: this.marked,
      selectedAnswer: this.selectedAnswer,
    };
  }

  importAnswer(answer: ExportAnswerMatching): void {
    if (answer.matches) {
      this.selectedAnswer = answer.matches.map((match) => ({
        left: match.left,
        right: match.right,
      }));
    } else {
      this.selectedAnswer = null;
    }
  }
  exportAnswer(): ExportAnswerMatching {
    return {
      matches: this.selectedAnswer,
    };
  }
}
